import type { ReactNode } from 'react';

import { Breadcrumb, Icon, type Crumb, type IconName } from '@/components/ui';
import { cn } from '@/lib/cn';

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Optional mark to the left of the title, in a bordered tile. */
  icon?: IconName;
  /** Right-aligned actions for the page, typically buttons. */
  actions?: ReactNode;
  /** Trail rendered above the title. Pass the crumbs, not a component. */
  breadcrumb?: Crumb[];
  /** Status badges or meta shown under the title. */
  meta?: ReactNode;
  className?: string;
}

/**
 * The heading block every page opens with.
 *
 * One component rather than each page rolling its own `<h1>`, so the type
 * scale, the spacing and the rule under the title stay identical everywhere -
 * which is what makes the app feel like one product rather than a set of
 * screens. The rule is what separates the heading from the content below it,
 * in the manner of a classic admin console.
 */
export function PageHeader({
  title,
  description,
  icon,
  actions,
  breadcrumb,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-3 border-b border-border pb-2.5', className)}>
      {breadcrumb && breadcrumb.length > 0 && <Breadcrumb items={breadcrumb} />}

      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-start gap-2.5">
          {icon && (
            <span className="mt-0.5 hidden rounded-sm border border-border bg-surface-muted p-1.5 text-content-muted sm:inline-flex">
              <Icon name={icon} size={16} />
            </span>
          )}

          <div className="min-w-0">
            <h1 className="text-base leading-tight font-bold tracking-tight text-content">
              {title}
            </h1>

            {description && (
              <p className="mt-0.5 text-xs leading-snug text-content-muted">{description}</p>
            )}

            {meta && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{meta}</div>}
          </div>
        </div>

        {actions && (
          /* `max-w-full` is what lets the wrapping work: `shrink-0` keeps the
             actions from being squeezed beside a long title, but without a
             ceiling it also let a row of three controls run off the side of a
             phone, where the last one could not be tapped. */
          <div className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-1.5">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
